/* global window */
/* global document */

/**
 * Abtra Shop
 * Developer: Abdelhady "Hady" Salah
 * File: App.js
 * Description: Front-end application main component
 * Last Updated: 2023/06/23
 */

import React, { Suspense, lazy, startTransition, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppContext from './components/context/AppContext';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import { Container, Row } from 'react-bootstrap';
import Homepage from './components/Homepage';
import AnnouncementBar from './components/AnnouncementBar';
import Footer from './components/Footer';
import FooterItem from './components/FooterItem';
import FooterItemBlock from './components/FooterItemBlock';
import FooterBlockLeft from './components/FooterBlockLeft';
import FooterBlockRight from './components/FooterBlockRight';
import WeAccept from './components/WeAccept';
import NewsletterBlock from './components/NewsletterBlock';
import BlogNews from './components/BlogNews';
import Article from './components/Article';
import SearchPage from './components/SearchPage';
import Collection from './components/Collection';
import { CartContext } from './components/context/CartContext';
import Page from './components/Page';
import NewsletterConfirmationPage from './components/NewsletterConfirmationPage';
import Product from './components/Product';
import { CustomerContext } from './components/context/CustomerContext';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingFallback from './components/LoadingFallback';
import SEO from './components/SEO';
import AccountRecoveryPage from './components/AccountRecoveryPage';
import AccountRecoveryConfirmationPage from './components/AccountRecoveryConfirmationPage';
import AccountRegistrationPage from './components/AccountRegistrationPage';
import AccountVerificationConfirmationPage from './components/AccountVerificationConfirmationPage';
import AccountVerificationPage from './components/AccountVerificationPage';
import Collections from './components/Collections';
import NotFound from './components/NotFound';
import CollectionVendors from './components/CollectionVendors';
import { Partytown } from '@builder.io/partytown/react';
import ShopperApprovedBlock from './components/ShopperApprovedBlock';
import RecentlyViewedProducts from './components/RecentlyViewedProducts';
import CartRecovery from './components/CartRecovery';
import HelpPage from './components/HelpPage';
import FaqPage from './components/FaqPage';
import constants from './constants.json' assert { type: 'json' };
import useDynamicPath from './hooks/useDynamicPath';
import Wallet from './components/Wallet';

// Lazy load

const Account = lazy(() => import('./components/Account'));
const AccountOrders = lazy(() => import('./components/AccountOrders'));
const AccountAddresses = lazy(() => import('./components/AccountAddresses'));
const AccountOrder = lazy(() => import('./components/AccountOrder'));
const CartPage = lazy(() => import('./components/CartPage'));
const ChangePassword = lazy(() => import('./components/ChangePassword'));

const App = ({ }) => {
    const { appContext } = useContext(AppContext);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const articlePath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.ARTICLE_PATH });
    const appConfig = appContext.config.themeConfig;
    const { shopperApprovedSealId, shopperApprovedReviewsUrl } = appContext.config;
    const {
        checkoutData,
        fetchCheckoutData,
    } = useContext(CartContext);
    const {
        customerData,
        fetchCustomerData,
    } = useContext(CustomerContext);
    const location = useLocation();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userFetched, setUserFetched] = useState(false);
    const [userFirstName, setUserFirstName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [navigationLinks, setNavigationLinks] = useState([]);
    const searchBoxRef = useRef(null);
    const [user, setUser] = useState(null);
    // Extract data from App Context
    const { userAgent } = appContext;
    // Get device type & handle scroll/resize
    const [width, setWidth] = useState(null);
    const [scrollY, setScrollY] = useState(0);
    // Identify mobile/desktop
    const [isMobile, setIsMobile] = useState(!!userAgent?.isMobile);
    useEffect(() => {
        setIsMobile(typeof width === 'number'
            ? width <= 768
            : !!userAgent?.isMobile);
    }, [width, userAgent.isMobile]);
    // Handle effects
    useEffect(() => {
        // Scroll to top on location changes
        document.documentElement.scrollTo(0, 0);
    }, [location.pathname]);
    // Handle scroll & width changes
    useEffect(() => {
        // Handle window resize (width)
        const handleWindowSizeChange = () => {
            startTransition(() => {
                setWidth(window.outerWidth > 0 ? window.outerWidth : window.screen.width);
            });
        };
        // Handle vertical scroll change
        const handleWindowScroll = () => {
            startTransition(() => {
                setScrollY(window.scrollY);
            });
        };
        handleWindowSizeChange();
        handleWindowScroll();
        window.addEventListener('resize', handleWindowSizeChange);
        window.addEventListener('scroll', handleWindowScroll);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
            window.removeEventListener('scroll', handleWindowScroll);
        };
    }, []);
    // Data fetching methods
    const fetchUserData = useCallback(async () => {
        try {
            const response = await fetch(new URL('api/user', process.env.APP_URL));
            const data = await response.json();
            if (data.user) {
                setUser(data.user);
                setUserFirstName(data.user.firstName ?? '');
                setUserLastName(data.user.lastName ?? '');
                setIsLoggedIn(true);
            } else {
                setIsLoggedIn(false);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setUserFetched(true);
        }
    }, []);
    const fetchNavigationData = useCallback(async () => {
        try {
            const response = await fetch(new URL('api/nav', process.env.APP_URL));
            const data = await response.json();
            setNavigationLinks(data?.navData ?? []);
        } catch (e) {
            console.error(e);
        }
    }, []);
    useEffect(() => {
        fetchUserData();
        fetchNavigationData();
        if (location.pathname !== process.env.CART_RECOVERY_PATH) {
            fetchCheckoutData();
        }
        fetchCustomerData();
    }, [fetchUserData, fetchNavigationData, fetchCheckoutData, fetchCustomerData]);
    // Handle redirect when in cart recovery mode
    useEffect(() => {
        const { recoveryMode } = checkoutData;
        if (recoveryMode === true) {
            window.location.assign(process.env.CART_RECOVERY_PATH);
        }
    }, [ checkoutData ]);
    // Render component
    return (<>
        <SEO
            seoTitle={appConfig.seoTitle ?? appContext.config.storeName}
            storeFaviconUrl={appContext.config.storeFaviconUrl}
            seoType={'website'}
            canonicalUrl={process.env.APP_URL}
        />
        <Partytown
            debug={process.env.NODE_ENV === 'development'}
            forward={['dataLayer.push']} />
        <div>
            <AnnouncementBar
                storefrontAnnouncement={appContext.config.storefrontAnnouncement} />
        </div>
        <header>
            <Navigation
                userFirstName={userFirstName}
                userLastName={userLastName}
                navData={navigationLinks}
                isMobile={isMobile}
                searchBoxRef={searchBoxRef}
                scrollY={scrollY} />
        </header>
        <Container
            as={'main'}
            style={{
                flexGrow: '1',
                margin: '0',
                minHeight: '100vh',
                padding: '0',
            }}
            fluid>
            <ErrorBoundary>
                <Suspense
                    fallback={<LoadingFallback />}>
                    <Row
                        style={{ margin: '0',
                            padding: '0' }}>
                        {/*<Sidebar />*/}
                        <div
                            className={'main-container__inner'}>
                            <Routes>
                                <Route
                                    exact
                                    path={'/account/orders/:id'}
                                    element={<AccountOrder
                                        isMobile={isMobile} />}></Route>
                                <Route
                                    path={'/account'}
                                    element={<Account />}>
                                    <Route
                                        index
                                        element={<AccountOrders
                                            orderList={customerData?.Orders ?? []} />}></Route>
                                    <Route
                                        path={'orders'}
                                        element={<AccountOrders
                                            orderList={customerData?.Orders ?? []} />}></Route>
                                    <Route
                                        path={'addresses'}
                                        element={<AccountAddresses
                                            addressList={customerData?.MailingAddresses ?? []} />}></Route>
                                </Route>
                                <Route
                                    exact
                                    path={'/'}
                                    element={<Homepage
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={articlePath[0].pathBeforeParams}
                                    element={<BlogNews
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={articlePath[0].fullPath}
                                    element={<Article
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={collectionPath[0].pathBeforeParams}
                                    element={<Collections
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={collectionPath[0].fullPath}
                                    element={<Collection
                                        searchBoxRef={searchBoxRef}
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={`${collectionPath[0].pathBeforeParams}/vendors`}
                                    element={<CollectionVendors
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={'/pages/search-results'}
                                    element={<SearchPage
                                        searchBoxRef={searchBoxRef}
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={'/pages/:handle'}
                                    element={<Page
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={'/confirm-subscription'}
                                    element={<NewsletterConfirmationPage
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={productPath[0].pathBeforeParams}
                                    element={<Collections
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={productPath[0].fullPath}
                                    element={<Product
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={`${collectionPath[0].pathBeforeParams}/:collectionHandle${productPath[0].pathBeforeParams}/:handle`}
                                    element={<Product
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={'/cart'}
                                    element={<CartPage
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={process.env.CART_RECOVERY_PATH}
                                    element={<CartRecovery
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={'/change-password'}
                                    element={<ChangePassword
                                        userFetched={userFetched}
                                        user={user} />} />
                                <Route
                                    exact
                                    path={'/reset-password'}
                                    element={<AccountRecoveryPage
                                        isMobile={isMobile}
                                        isLoggedIn={isLoggedIn} />} />
                                <Route
                                    exact
                                    path={'/recover-account'}
                                    element={<AccountRecoveryConfirmationPage
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={'/register'}
                                    element={<AccountRegistrationPage
                                        isMobile={isMobile}
                                        isLoggedIn={isLoggedIn} />} />
                                <Route
                                    exact
                                    path={'/start-verification'}
                                    element={<AccountVerificationPage
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={'/verify-account'}
                                    element={<AccountVerificationConfirmationPage
                                        isMobile={isMobile} />} />
                                <Route
                                    exact
                                    path={'/help'}
                                    element={<HelpPage />} />
                                <Route
                                    exact
                                    path={'/faq/:handle'}
                                    element={<FaqPage />} />
                                <Route
                                    exact
                                    path={'/wallet'}
                                    element={<Wallet
                                        user={user}
                                        isLoggedIn={isLoggedIn}
                                        userFetched={userFetched}/>} />
                                <Route
                                    path={'*'}
                                    element={<NotFound />}
                                />
                            </Routes>
                            {/* Recently viewed products */}
                            <RecentlyViewedProducts
                                isMobile={isMobile} />
                        </div>
                    </Row>
                </Suspense>
            </ErrorBoundary>
        </Container>
        <Footer
            isMobile={isMobile}>
            <FooterItem>
                {appConfig.footerBlocks
                    .map((block, idx) => (
                        <FooterItemBlock
                            key={idx}
                            blockTitle={block.blockTitle}
                            blockWidth={(100 / appConfig.footerBlocks.length)}
                            isMobile={isMobile}>
                            {block.blockContent?.length > 0 && (<p>{block.blockContent}</p>)}
                            {block.blockLinks?.length > 0 && (<ul
                                className={'block-links--list'}>
                                {block.blockLinks.map((link, linkIdx) => (<li
                                    className={'block-links--list-item'}
                                    key={linkIdx}>
                                    <Link
                                        to={link.url}>{link.text}</Link>
                                </li>))}
                            </ul>)}
                            {block.blockElements?.length > 0 && (
                                block.blockElements.map((blockElement, idx) => {
                                    if (blockElement === 'CUSTOM_BLOCK:NEWSLETTER')
                                        return (
                                            <NewsletterBlock
                                                key={idx}
                                                newsletterText={'Get it first. Sign up now for up-to-the-minute offers, sales and news.'} />
                                        );
                                    if (blockElement === 'CUSTOM_BLOCK:SHOPPER_APPROVED')
                                        return (
                                            <ShopperApprovedBlock
                                                key={idx}
                                                sealId={shopperApprovedSealId}
                                                reviewsUrl={shopperApprovedReviewsUrl} />
                                        );
                                    return (<></>);
                                })
                            )}
                        </FooterItemBlock>
                    ))}
            </FooterItem>
            <FooterItem>
                <FooterBlockLeft>
                    <nav
                        className={'footer-nav'}
                        aria-label={'Footer navigation'}>
                        <ul
                            className={'footer-nav--list'}>
                            {appConfig.footerNavLinks.map((navLink, idx) => (<li
                                className={'footer-nav--list-item'}
                                key={idx}>
                                <Link
                                    to={navLink.url}>{navLink.text}</Link>
                            </li>))}
                        </ul>
                    </nav>
                    <p
                        className={'footer-credits'}>{appConfig.copyrightNotice}</p>
                    <p
                        className={'footer-credits'}>Powered By Abtra Shop</p>
                </FooterBlockLeft>
                <FooterBlockRight>
                    <WeAccept />
                </FooterBlockRight>
            </FooterItem>
        </Footer>
    </>);
};

export default App;